import { Banner, DataTable, Text } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { useUpdateEffect } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { createSearchUrlParams } from 'app/views/layout/views/search/utils';
import { AppLink } from 'components/appLink';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { useUserQueues } from 'utils/user/useUserQueues';

import styles from '../../userDetails.module.scss';
import { makeColumns } from '../../utils/makeColumns';

const conversationsLoading = (
  <Text.Paragraph>Loading assigned conversations...</Text.Paragraph>
);

const conversationsFailed = (
  <Banner
    variant="fail"
    icon={null}
    description={
      <Text.Paragraph>
        An error occurred while loading assigned conversations
      </Text.Paragraph>
    }
  />
);

const conversationsEmpty = (
  <Banner
    variant="info"
    icon={null}
    description={
      <Text.Paragraph>
        There are no previous assigned conversations
      </Text.Paragraph>
    }
  />
);

export type UserAssignedConversationsProps = {
  user: User;
};

export function UserAssignedConversations({
  user,
}: UserAssignedConversationsProps): JSX.Element {
  const {
    items,
    total,
    data,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    changeArg,
  } = usePaginatedEndpoint({
    endpoint: conversationsApi.endpoints.readConversations,
    itemsSelector: ({ data: { conversations } }) => conversations,
    totalSelector: ({ data: { conversations_total_count } }) =>
      conversations_total_count || 0,
    initialArg: {
      agentIds: [user.id],
      limit: 6,
      offset: 0,
      showRestrictedCount: true,
    },
  });

  const userQueues = useUserQueues();

  useUpdateEffect(() => {
    changeArg({
      agentIds: [user.id],
      limit: 6,
      offset: 0,
    });
  }, [user.id, changeArg]);

  if (asyncStatus === 'rejected' || userQueues.asyncStatus === 'rejected') {
    return conversationsFailed;
  }

  if (
    userQueues.asyncStatus === 'pending' ||
    userQueues.asyncStatus === 'uninitialized'
  ) {
    return conversationsLoading;
  }

  if (!items.length) {
    if (asyncStatus === 'pending') {
      return conversationsLoading;
    }

    return conversationsEmpty;
  }

  return (
    <>
      {data.conversations_restricted_count ? (
        <Banner
          variant="warning"
          description={
            <Text.Paragraph>{`${data.conversations_restricted_count} assigned conversations in restricted queues excluded`}</Text.Paragraph>
          }
          className={styles.restrictedHitsBanner}
        />
      ) : null}
      <DataTable
        data={items}
        columns={makeColumns(
          ({ requester }) => requester,
          'Contact',
          userQueues,
        )}
        paginationProps={{
          loading: asyncStatus === 'pending',
          previousButtonDisabled: !hasPreviousPage,
          nextButtonDisabled: !hasNextPage,
          previousButtonClick: previousPage,
          nextButtonClick: nextPage,
        }}
      />

      <div className={styles.seeAllLinkContainer}>
        <AppLink
          path="/search/:scope"
          params={createSearchUrlParams({
            scope: 'conversations',
            query: '',
            filters: {
              strategy: 'all',
              conditions: [
                {
                  field: 'agent_id',
                  operator: 'is_one',
                  values: [{ label: '', value: user.id }],
                },
              ],
            },
          })}
          onClick={() => ampli.contactsCardSeeAllAssignedConversationsClicked()}
        >
          {`See all ${total ? `${total} ` : ''}${
            data.conversations_restricted_count != null &&
            data.conversations_restricted_count > 0
              ? 'unrestricted '
              : ''
          }conversations`}
        </AppLink>
      </div>
    </>
  );
}
